export interface ChartPeriod {
  offset: number
  start: string
  end: string
  vaults: Record<string | number, { closing_balance: number }>
}

export interface SeriesPoint {
  offset: number
  value: number
}

/** One vault's closing_balance across periods, chronological (ascending offset). */
export function buildVaultSeries(
  periods: ChartPeriod[],
  vaultId: number | string,
): SeriesPoint[] {
  return periods
    .filter((p) => p.vaults?.[vaultId]?.closing_balance !== undefined)
    .map((p) => ({ offset: p.offset, value: p.vaults[vaultId].closing_balance }))
    .sort((a, b) => a.offset - b.offset)
}

/**
 * Total = sum of closing_balance across selected vaults only, per period.
 * `selectedVaultIds === null` means "all vaults".
 */
export function buildTotalSeries(
  periods: ChartPeriod[],
  allVaultIds: Array<number | string>,
  selectedVaultIds: Array<number | string> | null,
): SeriesPoint[] {
  const ids = selectedVaultIds ?? allVaultIds
  return periods
    .map((p) => ({
      offset: p.offset,
      value: ids.reduce(
        (sum, id) => sum + (p.vaults?.[id]?.closing_balance ?? 0),
        0,
      ),
    }))
    .sort((a, b) => a.offset - b.offset)
}

/**
 * Splits a chronological series into its solid (actual) and dashed (forecast)
 * portions. Every period from the current period (offset 0) onward is
 * forecast; earlier periods are actual. The dashed segment shares its first
 * point with the solid segment's last point (the last closed period, offset
 * -1) so the two lines join with no gap — never a `today` date comparison.
 */
export function splitActualForecast(series: SeriesPoint[]): {
  solid: SeriesPoint[]
  dashed: SeriesPoint[]
} {
  const solid = series.filter((p) => p.offset < 0)
  const dashed = series.filter((p) => p.offset >= -1)
  return { solid, dashed }
}

/** The lowest point on a series, or null for an empty series. */
export function findLowPoint(series: SeriesPoint[]): SeriesPoint | null {
  if (series.length === 0) return null
  return series.reduce((min, p) => (p.value < min.value ? p : min), series[0])
}

export interface YAxisScale {
  min: number
  max: number
  ticks: number[]
}

/**
 * A "nice" rounded Y-axis scale with headroom, derived from the values
 * actually on screen (not a fixed range).
 */
export function computeYAxis(
  values: number[],
  { headroom = 0.12, tickCount = 5 } = {},
): YAxisScale {
  if (values.length === 0) {
    return { min: 0, max: 1, ticks: [0, 1] }
  }

  const rawMin = Math.min(0, ...values)
  const rawMax = Math.max(...values)
  const span = Math.max(rawMax - rawMin, 1)
  const paddedMin = rawMin - span * headroom
  const paddedMax = rawMax + span * headroom

  const step = niceStep((paddedMax - paddedMin) / Math.max(tickCount - 1, 1))
  const min = Math.floor(paddedMin / step) * step
  const max = Math.ceil(paddedMax / step) * step

  const ticks: number[] = []
  for (let v = min; v <= max + step / 2; v += step) {
    ticks.push(Math.round(v))
  }

  return { min, max, ticks }
}

/** Rounds a raw step to a "nice" 1/2/5 * 10^n value. */
function niceStep(rawStep: number): number {
  if (rawStep <= 0) return 1
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)))
  const residual = rawStep / magnitude
  let niceResidual: number
  if (residual <= 1) niceResidual = 1
  else if (residual <= 2) niceResidual = 2
  else if (residual <= 5) niceResidual = 5
  else niceResidual = 10
  return niceResidual * magnitude
}

/** Maps a value to an SVG y-coordinate within [0, height], inverted (higher value = lower y). */
export function yForValue(
  value: number,
  scale: YAxisScale,
  height: number,
): number {
  const range = scale.max - scale.min || 1
  return height - ((value - scale.min) / range) * height
}

/** Even column spacing across the full period count. */
export function xForOffsetIndex(index: number, count: number, width: number): number {
  if (count <= 1) return 0
  return (index / (count - 1)) * width
}

/** Builds an SVG path `d` string for a series of points, or '' if fewer than 2 points. */
export function toPath(
  points: SeriesPoint[],
  allOffsetsAscending: number[],
  scale: YAxisScale,
  width: number,
  height: number,
): string {
  if (points.length < 2) return ''
  const count = allOffsetsAscending.length
  return points
    .map((p) => {
      const index = allOffsetsAscending.indexOf(p.offset)
      const x = xForOffsetIndex(index, count, width)
      const y = yForValue(p.value, scale, height)
      return `${x},${y}`
    })
    .reduce((d, coord, i) => d + (i === 0 ? `M${coord}` : ` L${coord}`), '')
}
